/**
 * Residents (cutover remediation T2/T3, 2026-09-20): the door a resident
 * enters eMerge through without Monday, and the resolver's open doubts.
 *
 * The screen asks the resolver before it creates: "is this person known?"
 * The answer is one word and, when there are candidates, the candidates with
 * what matched and what differed — never a number. A row is created on
 * CREATE_NEW, or on the advocate's explicit, reasoned call when the resolver
 * says AMBIGUOUS. IDENTITY_CONFLICT is not the advocate's call: it goes to a
 * manager's identity review. The SSN travels once and is cleared.
 *
 * 2026-10-30: no resident had ever been created through this form. The
 * managers' checks answered CREATE_NEW and then nothing was sent: the Create
 * button was grey with no word why (the reason field was not marked
 * required), any edit after the check made it vanish, and the date of birth
 * refused a paste. Now: required fields say so; "Check and continue" is never
 * a silent grey button; every greyed-out act says why beside it; an edit after
 * the check keeps the buttons and asks for a new check; every refusal of the
 * create is said in words; a conflict can be sent to the Identity review a
 * manager rules on, on this same page; a replayed create reads as created.
 */
import React, { useEffect, useRef, useState } from 'react';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';
import DobInput from '../../components/emerge/DobInput';
import { advocateApi, freshKey, shortDate } from './advocateApi';
import type { Scope } from './advocateApi';
import {
  residentsApi, DECISION_LABEL, KIND_LABEL, signalWord,
  Candidate, CreateOutcome, IdentityDecision, IdentityScope, Resolution, Review, Signals,
} from './residentsApi';

const errText = (r: { error?: string; message?: string; code?: string; status?: number; retryAfterSeconds?: number }): string => {
  // 2026-10-05: a real 429 says nothing was changed and when to try again; the form keeps what was typed.
  if (r.status === 429) {
    const min = Math.max(1, Math.ceil((r.retryAfterSeconds && r.retryAfterSeconds > 0 ? r.retryAfterSeconds : 900) / 60));
    return `Nothing was changed. Too many requests from your account in the last few minutes. Wait about ${min} minute${min === 1 ? '' : 's'}, then try again. Your entry is still here.`;
  }
  return r.message || r.error || (r.code ? `The server refused this (${r.code}).` : 'The server refused this.');
};

/** T3 (charter §33): an empty queue is not truth when its source failed. Shown only when the state changes what to do. */
export function SourceHealthNotice({ scope }: { scope: Scope }) {
  const h = scope.source_health;
  if (!h || !h.notice) return null;
  return (
    <div className={`pa-note pa-fresh${h.notice.tone === 'critical' ? ' stop' : ''}`} role={h.notice.tone === 'critical' ? 'alert' : 'status'}>
      <span><strong>CounselEAR: {h.state.toLowerCase()}.</strong> {h.notice.text}</span>
    </div>
  );
}

function SignalList({ s }: { s: Signals | null }) {
  if (!s) return null;
  const rows: [string, keyof Signals][] = [['SSN', 'ssn'], ['CounselEAR id', 'counselear_id'], ['Date of birth', 'dob'], ['Last name', 'last_name'], ['First name', 'first_name']];
  return (
    <ul className="pa-signals" aria-label="What matched">
      {rows.filter(([, k]) => s[k] !== null && s[k] !== undefined).map(([label, k]) => <li key={k} className={s[k] === true ? 'ok' : 'stop'}>{label} {signalWord(s[k] ?? null)}</li>)}
    </ul>
  );
}

function CandidateCard({ c, action, note }: { c: Candidate; action?: React.ReactNode; note?: string | null }) {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Name not recorded';
  return (
    <li className="pa-candidate">
      <div>
        <strong>{name}</strong>
        <span className="pa-text none"> · DOB {c.dob ? shortDate(c.dob) : 'not recorded'} · CounselEAR {c.counselear_id || 'none'}{'ssn_masked' in c ? ` · SSN ${c.ssn_masked || 'none on file'}` : ''}</span>
        <span className="pa-text none"> · <BuildingOrClinic row={c} fallback="building not recorded" /> · {c.source_system === 'EMERGE' ? 'created in eMerge' : 'from the board'}</span>
      </div>
      <SignalList s={c.signals} />
      {note && <p className="pa-text" aria-label="What eMerge found">{note}</p>}
      {action}
    </li>
  );
}

/** MM/DD/YYYY from an ISO date, as the person typed it -- no time zone can move the day. */
const usDate = (iso: string | null | undefined): string => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : 'not recorded');
const joinWords = (w: string[]): string => (w.length <= 1 ? w.join('') : `${w.slice(0, -1).join(', ')} and ${w[w.length - 1]}`);
/** What the person entered when they checked. */
export type CheckedFacts = { first: string; last: string; dob: string; ceId: string };

/**
 * What the resolver found about one candidate, in a sentence a person can act on (2026-10-30). A PA
 * manager met a conflict whose only words were "a PA manager resolves this": she IS one, and the card
 * said neither what differed nor what to do. The signals are flags; the values beside them are the
 * candidate's (what the card shows anyway) and what she typed.
 */
export function differenceLine(c: Candidate, e: CheckedFacts, decision: IdentityDecision): string | null {
  const s = c.signals;
  if (!s) return null;
  const same: string[] = [];
  if (s.ssn === true) same.push('SSN');
  if (s.counselear_id === true) same.push('CounselEAR id');
  if (s.first_name === true && s.last_name === true) same.push('name');
  else if (s.last_name === true) same.push('last name');
  else if (s.first_name === true) same.push('first name');
  if (s.dob === true) same.push('date of birth');
  const diffs: string[] = [];
  if (s.dob === false) diffs.push(`Date of birth on file ${usDate(c.dob)}; you entered ${usDate(e.dob)}.`);
  if (s.counselear_id === false) diffs.push(`CounselEAR id on file ${c.counselear_id || 'none'}; you entered ${e.ceId || 'none'}.`);
  if (s.ssn === false) diffs.push(`The SSN on file${c.ssn_masked ? ` (${c.ssn_masked})` : ''} is not the one you entered.`);
  if (s.first_name === false) diffs.push(`First name on file ${c.first_name || 'not recorded'}; you entered ${e.first || 'none'}.`);
  if (s.last_name === false) diffs.push(`Last name on file ${c.last_name || 'not recorded'}; you entered ${e.last || 'none'}.`);
  const head = decision === 'AMBIGUOUS' ? 'Possibly this person' : 'Already in eMerge';
  const found = same.length ? `${head}: same ${joinWords(same)}.` : `${head}.`;
  const tail = !diffs.length ? '' : decision === 'AMBIGUOUS' ? ' If this is the same person, one of them is a typo.' : diffs.length === 1 ? ' One of them is a typo.' : ' Check which is right.';
  return `${found}${diffs.length ? ` ${diffs.join(' ')}` : ''}${tail}`;
}

/** A resident the form can show or open: who they are and their PA case, when they have one. */
export type ResidentTarget = {
  patientId: string; name: string; dob: string | null; counselearId: string | null; building: string | null;
  caseId: string | null; source: 'MONDAY' | 'EMERGE'; createdNow?: boolean;
};
const targetOf = (c: Candidate): ResidentTarget => ({
  patientId: c.id, name: fullName(c) || 'Name not recorded', dob: c.dob, counselearId: c.counselear_id || null,
  building: c.facility_name || (c.counselear_clinic_name ? `CounselEAR clinic: ${c.counselear_clinic_name} (building not confirmed)` : null),
  caseId: c.case_id || null, source: c.source_system,
});

/**
 * "Open this resident" opened nothing (2026-10-30, a PA manager after correcting a date of birth): the shell
 * dropped the id and showed a notice. Now a resident with a PA case opens it in the docked case panel; one with
 * none is shown here, saying so -- never a click that does nothing.
 */
function ResidentCard({ t, onOpenCase }: { t: ResidentTarget; onOpenCase?: (t: ResidentTarget) => void }) {
  return (
    <section className="pa-section" role="region" aria-label={`Resident: ${t.name}`} tabIndex={-1} id="new-resident-card">
      <h3>{t.name}</h3>
      <ul className="pa-signals" aria-label="On file">
        <li>Date of birth {t.dob ? usDate(t.dob) : 'not recorded'}</li>
        <li>CounselEAR id {t.counselearId || 'none'}</li>
        <li>{t.building || 'No building on file'}</li>
        <li>{t.createdNow ? 'Created in eMerge just now' : t.source === 'EMERGE' ? 'Created in eMerge' : 'From the board'}</li>
      </ul>
      {t.caseId && onOpenCase
        ? <div className="btns"><button type="button" className="pa-open pa-primary" onClick={() => onOpenCase(t)}>Open their case</button></div>
        : t.caseId ? <p className="pa-text">They have a PA case: find them by name in My work or Team cases.</p>
        : (
          <p className="pa-text">
            No PA case yet. {t.counselearId
              ? 'A PA case opens for them when CounselEAR shows the TWL tag for their CounselEAR id.'
              : 'Without a CounselEAR id, CounselEAR\'s tags cannot reach them, so no PA case opens for them on its own.'}
          </p>
        )}
    </section>
  );
}

/** When the correction did not close the review it was asked to (2026-10-30): why, in words, never a code. */
export const REVIEW_LEFT_OPEN: Record<string, string> = {
  NOT_OPEN: 'The Identity review about them was already ruled on, so it was left as it is.',
  NOT_A_PA_REVIEW: 'This is not a PA create review: it stays open; rule on it in Identity review.',
  NOT_ABOUT_THIS_RESIDENT: 'The Identity review shown is not about this resident, so it stays open: rule on it in Identity review.',
  NO_SUCH_REVIEW: 'The Identity review could not be found, so nothing was closed.',
  NOT_CHANGED: 'Nothing changed, so the Identity review about them stays open: rule on it in Identity review.',
  REVIEW_WRITES_CLOSED: 'Ruling on Identity reviews is not open on this server, so the review stays open.',
  DEFAULT: 'The Identity review about them stayed open: rule on it in Identity review.',
};

export const DOB_FIX_REASON = 'Date of birth corrected from the entered value (CounselEAR)';

/** A refusal of the case's date-of-birth correction, in words. */
function dobRefusalWords(r: { error?: string; message?: string; code?: string; status?: number; retryAfterSeconds?: number }): string {
  if (r.status === 429) return errText(r);
  switch (r.code) {
    case 'REASON_REQUIRED': return 'Nothing was changed. Say why the date of birth is being corrected.';
    case 'DOB_INVALID': case 'DOB_FUTURE': case 'DOB_IMPLAUSIBLE': return `Nothing was changed. ${r.message || 'That date of birth is not accepted.'}`;
    case 'FIELD_AUTHORITY_DENIED': return 'Nothing was changed. eMerge may not write the date of birth on this resident yet. Tell an administrator.';
    case 'NO_RESIDENT': return 'Nothing was changed. That case is not linked to this resident.';
    case 'NOT_FOUND': return 'Nothing was changed. Their case was not found; check again.';
    default: break;
  }
  if (r.status === 403 || (r.code && /^DENY_/.test(r.code))) return 'Nothing was changed. Your account may not correct this resident\'s date of birth.';
  if (r.status === 404) return 'Nothing was changed. Correcting a date of birth is not open on this server. Tell an administrator.';
  if (r.status && r.status >= 500) return 'Nothing was changed. The server could not finish this. Try again in a minute.';
  return r.message ? `Nothing was changed. ${r.message}` : errText(r);
}

/**
 * Correct the candidate's date of birth to the one entered: the EXISTING case-scoped act (the Identity
 * tab's "Correct date of birth", POST /advocate/cases/:id/dob), with a reason; the prior value is kept as a
 * correction on the record. Never a write of its own.
 */
function CorrectDobInline({ c, to, onDone, onCancel, closeReview = false }: { c: Candidate; to: string; onDone: (words: string) => void; onCancel: () => void; closeReview?: boolean }) {
  const [why, setWhy] = useState(DOB_FIX_REASON);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(freshKey());
  const go = async () => {
    if (!why.trim()) { setErr('Say why the date of birth is being corrected.'); return; }
    if (!c.case_id) return;
    setBusy(true); setErr(null);
    try {
      // The Identity review about this conflict, when there is one and the caller rules on reviews, is closed with it.
      const reviewId = closeReview && c.open_review_id ? c.open_review_id : null;
      const r = await advocateApi.correctDob(c.case_id, { dob: to, reason: why.trim(), idempotency_key: key.current, ...(reviewId ? { resolves_review_id: reviewId } : {}) });
      if (!r.success || !r.data) { setErr(dobRefusalWords(r)); return; }
      const closed = !reviewId || !r.data.review ? ''
        : r.data.review.resolved ? ' The Identity review about them is closed as the same person.'
          : ` ${REVIEW_LEFT_OPEN[r.data.review.why || ''] || REVIEW_LEFT_OPEN.DEFAULT}`;
      onDone(r.data.changed === false
        ? `Their date of birth already reads ${usDate(to)}. Nothing was changed.${closed}`
        : `Their date of birth is now ${usDate(to)}; the prior value ${usDate(c.dob)} is kept on their record.${closed}`);
    } catch { setErr('Could not reach the server. If it went through, pressing again does not correct it twice.'); } finally { setBusy(false); }
  };
  return (
    <div className="pa-form" role="group" aria-label="Correct their date of birth">
      <label><span>Why (required)</span><input value={why} onChange={(e) => setWhy(e.target.value)} maxLength={2000} aria-label="Why the date of birth is corrected (required)" aria-required="true" /></label>
      <div className="btns">
        <button type="button" className="pa-open pa-primary" disabled={busy} onClick={go}>{busy ? 'Correcting…' : `Confirm: set their date of birth to ${usDate(to)}`}</button>
        <button type="button" className="pa-link" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </div>
  );
}

/**
 * What a refusal of the check or the create means, in words (2026-10-30). Every code the
 * create route answers is here; anything else falls back to the server's own message.
 */
export function createRefusalWords(r: { error?: string; message?: string; code?: string; status?: number; retryAfterSeconds?: number }, opts: { reviewer?: boolean } = {}): string {
  if (r.status === 429) return errText(r);
  const here = opts.reviewer ? ' You can rule on it in Identity review, lower on this page.' : ' A PA manager rules on it in Identity review.';
  switch (r.code) {
    case 'REASON_REQUIRED': return 'Nothing was created. Say how the person came to be known, then press the button again.';
    case 'NAME_REQUIRED': return 'Nothing was created. Enter the first and the last name.';
    case 'DOB_REQUIRED': return 'Nothing was created. Enter the date of birth: it is how the next person tells two residents apart.';
    case 'DOB_INVALID': return 'Nothing was created. eMerge could not read that date of birth. Type it as MM/DD/YYYY; it cannot be in the future or before 1900.';
    case 'COUNSELEAR_ID_INVALID': return 'Nothing was created. A CounselEAR id is 1 to 12 digits. Leave it empty if you do not know it.';
    case 'SSN_INVALID': return 'Nothing was created. That is not a valid SSN. Check the nine digits, or leave the SSN empty.';
    case 'FACILITY_INVALID':
    case 'FACILITY_NOT_FOUND': return 'Nothing was created. That building was not found. Choose it again from the list, or leave Building empty.';
    case 'OVERRIDE_INVALID':
    case 'CANDIDATE_REQUIRED':
    case 'CANDIDATE_NOT_OFFERED': return 'Nothing was created. The residents eMerge found have changed since you checked. Press "Check and continue" again.';
    case 'IDENTITY_CONFLICT': return `Nothing was created. The SSN or CounselEAR id you entered belongs to a resident already in eMerge, and another detail disagrees, so eMerge will not create a second resident on it. If you mistyped it, correct it and check again; otherwise send it to Identity review.${here}`;
    case 'IDENTITY_REVIEW_OPEN': return `Nothing was created. This CounselEAR id is waiting in Identity review: who the person is gets decided there first.${here}`;
    case 'IDENTITY_REVIEW_RULED': return 'Nothing was created. A manager has already ruled that this CounselEAR id belongs to a resident in eMerge (or waived it), so it is not given to a new resident. Check the CounselEAR id, or leave it empty.';
    case 'IDEMPOTENCY_KEY_INVALID':
    case 'IDEMPOTENCY_KEY_REQUIRED':
    case 'IDEMPOTENCY_KEY_REUSED': return 'Nothing was created. The form\'s request was not accepted; press the button again.';
    case 'IDENTITY_FACTS_REQUIRED': return 'Enter the first name, the last name and the date of birth, then check again.';
    case 'ENCRYPT_FAILED':
    case 'SSN_MATCH_KEY_UNCONFIGURED': return 'Nothing was created. The server could not protect the SSN. Leave the SSN empty to go on without it, and tell an administrator.';
    case 'FIELD_AUTHORITY_DENIED': return 'Nothing was created. eMerge may not write one of these fields yet. Tell an administrator.';
    case 'ACTOR_REQUIRED': return 'Nothing was created. Your sign-in has ended. Sign in again; then enter the resident again.';
    case 'EMERGE_PHI_ACCESS_DENIED': return 'Nothing was created. Your account is not cleared for resident records on this server. Ask an administrator.';
    default: break;
  }
  if (r.status === 403 || (r.code && /^DENY_/.test(r.code))) return 'Nothing was created. Your account may not create residents. Ask an administrator for the PA or PA manager role.';
  if (r.status === 404) return 'Nothing was created. Creating residents is not open on this server. Tell an administrator.';
  if (r.status && r.status >= 500) return 'Nothing was created. The server could not finish this. Try again in a minute; if it keeps failing, tell an administrator.';
  return r.message ? `Nothing was created. ${r.message}` : errText(r);
}

type Fields = { first: string; last: string; dob: string; ceId: string; ssn: string };
/** What the resolver was asked. A change to any of these after a check needs a new check. */
const signature = (f: Fields): string => JSON.stringify([f.first.trim(), f.last.trim(), f.dob, f.ceId.trim(), f.ssn.replace(/\D/g, '')]);
const fullName = (c: { first_name: string | null; last_name: string | null }) => [c.first_name, c.last_name].filter(Boolean).join(' ');

const DECISION_WORDS: Record<IdentityDecision, { title: string; text: string }> = {
  CREATE_NEW: { title: 'Not in eMerge yet: you can create this resident', text: 'Nobody in eMerge has this SSN, this CounselEAR id, or this last name with this date of birth.' },
  MATCH_EXISTING: { title: 'This person is already in eMerge', text: 'The SSN or the CounselEAR id belongs to the resident below, and nothing disagrees. Nothing will be created: open their record instead.' },
  AMBIGUOUS: { title: 'Possibly already in eMerge: your call', text: 'eMerge holds someone with the same last name and date of birth. If one of them is this person, press "This is the same person" on their card: nothing is created and you are pointed at their record. If none of them is, press "Different person — create anyway": a new resident is created and your reason is kept with it. Not sure? Send it to Identity review for a PA manager.' },
  IDENTITY_CONFLICT: { title: 'Already in eMerge, but a detail disagrees', text: 'The SSN or CounselEAR id you entered belongs to the resident below, but another detail does not agree. eMerge will not create a second resident on that evidence. Decide which detail is right: open the resident, correct their record to what you entered, or keep what is on file. If you mistyped the SSN or CounselEAR id, correct it above and check again. Not sure? Send it to Identity review: a PA manager decides there whether this is the same person.' },
};

export function NewResidentForm({ onCreated, onOpen, reviewer = false, onSentToReview, canCorrectDob = false }: {
  onCreated: (patientId: string, msg: string) => void;
  /** Opens a resident's PA case in the docked case panel. A resident with no case is shown on this form instead. */
  onOpen?: (t: ResidentTarget) => void;
  /** The caller may correct a resident's date of birth on their case (a PA manager with the demographics write open). */
  canCorrectDob?: boolean;
  /** The caller rules on identity reviews, so a review can be reached on this page. */
  reviewer?: boolean;
  /** The identity reviews changed (one was opened, or an act closed one): the review list on the page is read again. */
  onSentToReview?: () => void;
}) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [dob, setDob] = useState('');
  const [dobProblem, setDobProblem] = useState<string | null>(null);
  const [ceId, setCeId] = useState('');
  const [ssn, setSsn] = useState('');
  const [ssnCleared, setSsnCleared] = useState(false);
  const [reason, setReason] = useState('');
  const [fq, setFq] = useState('');
  const [hits, setHits] = useState<{ id: string; name: string; city: string | null; state: string | null }[]>([]);
  const [hitState, setHitState] = useState<'idle' | 'loading' | 'done' | 'failed'>('idle');
  const [facility, setFacility] = useState<{ id: string; name: string } | null>(null);
  const [resolution, setResolution] = useState<Resolution | null>(null);
  const [checkedSig, setCheckedSig] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CreateOutcome | null>(null);
  const [done, setDone] = useState<{ target: ResidentTarget; text: string; created: boolean } | null>(null);
  const [sentToReview, setSentToReview] = useState(false);
  const [checked, setChecked] = useState<CheckedFacts | null>(null);
  const [fixing, setFixing] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [card, setCard] = useState<ResidentTarget | null>(null);
  /** Open a resident: their case when they have one (and the shell can dock it), else their card here. */
  const openResident = (t: ResidentTarget) => {
    if (t.caseId && onOpen) { onOpen(t); return; }
    setCard(t);
    window.setTimeout(() => { const el = document.getElementById('new-resident-card'); if (el) { el.focus(); if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' }); } }, 0);
  };
  const [busy, setBusy] = useState<null | 'check' | 'create'>(null);
  const [err, setErr] = useState<string | null>(null);
  // One key per person and building: a retry of the same press replays it; a different person never does.
  const keyRef = useRef<{ sig: string; key: string } | null>(null);
  const firstRef = useRef<HTMLInputElement | null>(null);
  const reasonRef = useRef<HTMLInputElement | null>(null);
  const decisionRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (fq.trim().length < 2) { setHits([]); setHitState('idle'); return; }
    let live = true;
    setHitState('loading');
    (async () => {
      try {
        const r = await advocateApi.facilities(fq.trim());
        if (!live) return;
        if (r.success && r.data) { setHits(r.data.items); setHitState('done'); } else { setHits([]); setHitState('failed'); }
      } catch { if (live) { setHits([]); setHitState('failed'); } }
    })();
    return () => { live = false; };
  }, [fq]);

  const fields: Fields = { first, last, dob, ceId, ssn };
  const sig = signature(fields);
  const stale = Boolean(resolution) && checkedSig !== sig;
  const facts = () => ({ first_name: first.trim(), last_name: last.trim(), dob, counselear_id: ceId.trim() || undefined, ssn: ssn.replace(/\D/g, '') ? ssn : undefined });
  const clearSecret = () => { if (ssn) setSsnCleared(true); setSsn(''); };
  const focusReason = () => { reasonRef.current?.focus(); };

  /** What stops the check, in words; null when it may run. */
  const checkBlocker = (): string | null => {
    const missing = [!first.trim() && 'the first name', !last.trim() && 'the last name', !dob && !dobProblem && 'the date of birth'].filter(Boolean) as string[];
    if (missing.length) return `Before checking, enter ${missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`}.`;
    if (!dob && dobProblem) return `The date of birth: ${dobProblem}`;
    if (ceId.trim() && !/^\d{1,12}$/.test(ceId.trim())) return 'CounselEAR id must contain 1 to 12 digits.';
    return null;
  };

  const resolve = async (keepInfo = false) => {
    const blocked = checkBlocker();
    if (blocked) { setErr(blocked); return; }
    setBusy('check'); setErr(null); if (!keepInfo) setInfo(null); setOutcome(null); setDone(null); setSentToReview(false); setFixing(null); setCard(null);
    try {
      const r = await residentsApi.resolve(facts());
      if (!r.success || !r.data) { setErr(createRefusalWords(r, { reviewer })); setResolution(null); setCheckedSig(null); return; }
      setResolution(r.data); setCheckedSig(sig); setSsnCleared(false);
      setChecked({ first: first.trim(), last: last.trim(), dob, ceId: ceId.trim() });
      window.setTimeout(() => { const h = decisionRef.current; if (h) { h.focus(); if (typeof h.scrollIntoView === 'function') h.scrollIntoView({ block: 'nearest' }); } }, 0);
    } catch { setErr('Could not reach the server. Nothing was checked; try again.'); } finally { setBusy(null); }
  };

  const create = async (override?: 'CREATE_ANYWAY' | 'USE_CANDIDATE', candidateId?: string) => {
    const keySig = `${sig}|${facility?.id || ''}`;
    if (!keyRef.current || keyRef.current.sig !== keySig) keyRef.current = { sig: keySig, key: freshKey() };
    setBusy('create'); setErr(null);
    const name = `${first.trim()} ${last.trim()}`;
    try {
      const r = await residentsApi.create({ ...facts(), facility_id: facility?.id, reason: reason.trim(), override: override || null, override_candidate_id: candidateId || null, idempotency_key: keyRef.current.key });
      clearSecret();
      if (!r.success || !r.data) {
        if (r.code === 'IDEMPOTENCY_KEY_INVALID' || r.code === 'IDEMPOTENCY_KEY_REQUIRED' || r.code === 'IDEMPOTENCY_KEY_REUSED') keyRef.current = null;
        setErr(createRefusalWords(r, { reviewer }));
        return;
      }
      setOutcome(r.data);
      const made = Boolean(r.data.patient_id) && (r.data.created || r.data.replayed === true);
      if (made) {
        const text = r.data.replayed
          ? `${name} was already created a moment ago; nothing was created twice.`
          : 'Resident created.';
        setDone({
          target: { patientId: r.data.patient_id!, name, dob: dob || null, counselearId: ceId.trim() || null, building: facility?.name || null, caseId: null, source: 'EMERGE', createdNow: true },
          text, created: true,
        });
        // A "create anyway" may have closed the review its own attempt opened: the review list below is read again.
        if (override === 'CREATE_ANYWAY' && onSentToReview) onSentToReview();
        keyRef.current = null;
        onCreated(r.data.patient_id!, r.data.replayed ? 'That resident was already created; nothing was created twice.' : 'Resident created in eMerge. CounselEAR tags and the intake rule pick them up on the next half-hour.');
      } else if (r.data.patient_id && r.data.decision === 'MATCH_EXISTING') {
        const match = (r.data.candidates || resolution?.candidates || []).find((c) => c.id === r.data!.patient_id);
        const target: ResidentTarget = match ? targetOf(match) : { patientId: r.data.patient_id, name, dob: dob || null, counselearId: ceId.trim() || null, building: null, caseId: null, source: 'MONDAY' };
        setDone({ target, text: 'Recorded: this is a resident already in eMerge. Nothing was created.', created: false });
        keyRef.current = null;
        onCreated(r.data.patient_id, 'That resident already exists; nothing was created.');
      } else if (r.data.decision === 'AMBIGUOUS' || r.data.decision === 'IDENTITY_CONFLICT') {
        // The server recorded an OPEN review with the reason; the resolver's answer is the newest one.
        setResolution((prev) => (prev ? { ...prev, decision: r.data!.decision, candidates: r.data!.candidates || prev.candidates } : prev));
        setSentToReview(true);
        if (onSentToReview) onSentToReview();
      }
    } catch {
      clearSecret();
      setErr('Could not reach the server. If the resident was created before the connection dropped, pressing the button again will not create them twice.');
    } finally { setBusy(null); }
  };

  const startNext = () => {
    setFirst(''); setLast(''); setDob(''); setDobProblem(null); setCeId(''); setSsn(''); setSsnCleared(false); setReason('');
    setFq(''); setFacility(null); setHits([]); setResolution(null); setCheckedSig(null); setOutcome(null); setDone(null); setSentToReview(false); setErr(null);
    setChecked(null); setFixing(null); setInfo(null); setCard(null);
    keyRef.current = null;
    window.setTimeout(() => firstRef.current?.focus(), 0);
  };

  const d: IdentityDecision | null = resolution?.decision || null;
  const candidates = (outcome?.candidates && outcome.candidates.length ? outcome.candidates : resolution?.candidates) || [];
  // Why a create button is greyed out, said beside it -- never a silent disabled button.
  const createBlocker = busy ? null
    : stale ? (ssnCleared && !ssn ? 'The SSN is cleared after each attempt. Enter it again (or leave it empty) and check again.' : 'You changed a field: check again.')
      : !reason.trim() ? 'Say how the person came to be known first.' : null;
  const canAct = !busy && !createBlocker;
  const blockerLine = createBlocker && (
    <p className="pa-text none" id="new-resident-blocked" role="status">
      {createBlocker}{' '}
      {!stale && <button type="button" className="pa-link" onClick={focusReason}>Write the reason</button>}
      {stale && <button type="button" className="pa-link" onClick={() => { void resolve(); }}>Check again</button>}
    </p>
  );

  return (
    <section className="pa-card" aria-label="New resident">
      <div className="pa-card-head"><h2>New resident</h2><span className="why">For a person met at a building or found in CounselEAR who is not in eMerge yet. Fill in the fields marked required, press "Check and continue", then create.</span></div>
      <form className="pa-form" autoComplete="off" noValidate onSubmit={(e) => { e.preventDefault(); resolve(); }}>
        <div className="pa-grid2">
          <label><span>First name (required)</span><input ref={firstRef} id="resident-first-name" name="first_name" autoComplete="given-name" value={first} onChange={(e) => setFirst(e.target.value)} maxLength={120} aria-label="First name (required)" aria-required="true" /></label>
          <label><span>Last name (required)</span><input id="resident-last-name" name="last_name" autoComplete="family-name" value={last} onChange={(e) => setLast(e.target.value)} maxLength={120} aria-label="Last name (required)" aria-required="true" /></label>
          <label><span>Date of birth (required)</span><DobInput id="resident-dob" name="dob" value={dob} onChange={(iso, problem) => { setDob(iso); setDobProblem(problem); }} aria-label="Date of birth (required)" required hintClassName="pa-text none" /></label>
          <label><span>CounselEAR id (if known)</span><input id="resident-counselear-id" name="counselear_id" autoComplete="off" value={ceId} inputMode="numeric" onChange={(e) => setCeId(e.target.value)} maxLength={12} aria-label="CounselEAR id (if known)" /></label>
          <label><span>SSN (if known)</span><input id="resident-ssn" name="ssn" type="password" inputMode="numeric" value={ssn} onChange={(e) => { setSsn(e.target.value); if (e.target.value) setSsnCleared(false); }} maxLength={11} aria-label="SSN (if known)" autoComplete="off" /></label>
          <label><span>Building (optional)</span><input value={facility ? facility.name : fq} onChange={(e) => { setFacility(null); setFq(e.target.value); }} placeholder="Type the building's name" aria-label="Building (optional)" /></label>
        </div>
        {!facility && hits.length > 0 && (
          <ul className="pa-hits" aria-label="Buildings">{hits.map((h) => <li key={h.id}><button type="button" className="pa-link" onClick={() => { setFacility({ id: h.id, name: h.name }); setHits([]); }}>{h.name}{h.city ? ` · ${h.city}${h.state ? `, ${h.state}` : ''}` : ''}</button></li>)}</ul>
        )}
        {!facility && hitState === 'done' && hits.length === 0 && <p className="pa-text none" role="status">No building by that name. Leave Building empty to create the resident without one.</p>}
        {!facility && hitState === 'failed' && <p className="pa-text none" role="status">The building search failed. Leave Building empty to create the resident without one, or try again.</p>}
        <label><span>How the person came to be known (required)</span><input ref={reasonRef} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="How the person came to be known (required)" aria-required="true" placeholder="For example: met at the building during rounds" /></label>
        <p className="pa-text none">The SSN is encrypted on entry and never shown here. eMerge checks first whether this person already exists, then you create them.</p>
        <div className="btns"><button type="submit" className="pa-open pa-primary" disabled={Boolean(busy)}>{busy === 'check' ? 'Checking…' : 'Check and continue'}</button></div>
        {err && <p className="pa-notice stop" role="alert">{err}</p>}
        {info && <p className="pa-notice" role="status">{info}</p>}
      </form>

      {done && (
        <div className="pa-section" aria-label="Result">
          <p className="pa-notice ok" role="status">
            {done.text} Open: <button type="button" className="pa-link" onClick={() => openResident(done.target)}>{done.target.name}</button>
          </p>
          <div className="btns">
            <button type="button" className="pa-open pa-primary" onClick={startNext}>Add another resident</button>
          </div>
        </div>
      )}

      {d && !done && (
        <div className="pa-section" aria-label="Identity decision">
          <h3 ref={decisionRef} tabIndex={-1}>{DECISION_WORDS[d].title}</h3>
          <p className="pa-text">{DECISION_WORDS[d].text}</p>
          {stale && <p className="pa-notice stop" role="status">You changed a field: check again. The answer below is for what you entered before.</p>}
          {candidates.length > 0 && (
            <ul className="pa-candidates" aria-label="Candidates">
              {candidates.map((c) => {
                const name = fullName(c) || `${first.trim()} ${last.trim()}`;
                const open = () => openResident({ ...targetOf(c), name });
                // Correct their record to what was entered: only a date of birth, only by a PA manager, only on their case.
                const dobDiffers = d === 'IDENTITY_CONFLICT' && c.signals?.dob === false && Boolean(checked?.dob) && !stale;
                const reviewLink = reviewer && c.open_review_id ? (
                  <a className="pa-link" href={`#review-${c.open_review_id}`} onClick={(e) => { e.preventDefault(); const el = document.getElementById(`review-${c.open_review_id}`) || document.getElementById('identity-review'); if (el) { el.focus(); if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' }); } }}>Go to the Identity review about them</a>
                ) : null;
                return (
                  <CandidateCard key={c.id} c={c} note={checked ? differenceLine(c, checked, d) : null} action={
                    <>
                      <div className="btns">
                        {d === 'AMBIGUOUS' && <button type="button" className="pa-open" disabled={!canAct} aria-describedby={createBlocker ? 'new-resident-blocked' : undefined} onClick={() => create('USE_CANDIDATE', c.id)}>This is the same person</button>}
                        <button type="button" className="pa-open" onClick={open}>Open this resident</button>
                        {dobDiffers && canCorrectDob && c.case_id && fixing !== c.id && <button type="button" className="pa-open" onClick={() => setFixing(c.id)}>Correct their date of birth to {usDate(checked!.dob)}</button>}
                        {d === 'IDENTITY_CONFLICT' && <button type="button" className="pa-link" onClick={() => { setResolution(null); setCheckedSig(null); setChecked(null); setFixing(null); setSentToReview(false); setInfo('Kept what is on file. Nothing was changed or created.'); }}>Keep what is on file</button>}
                        {reviewLink}
                      </div>
                      {dobDiffers && canCorrectDob && !c.case_id && <p className="pa-text none">They have no PA case, and eMerge corrects a date of birth only on a case, so it cannot be corrected from here yet.</p>}
                      {dobDiffers && !canCorrectDob && <p className="pa-text none">A PA manager can correct their date of birth on their case.</p>}
                      {fixing === c.id && checked && (
                        <CorrectDobInline c={c} to={checked.dob} closeReview={reviewer} onCancel={() => setFixing(null)}
                          onDone={(words) => { setFixing(null); setInfo(words); void resolve(true); if (onSentToReview) onSentToReview(); }} />
                      )}
                    </>
                  } />
                );
              })}
            </ul>
          )}
          {d === 'CREATE_NEW' && (
            <div className="btns"><button type="button" className="pa-open pa-primary" disabled={!canAct} aria-describedby={createBlocker ? 'new-resident-blocked' : undefined} onClick={() => create()}>{busy === 'create' ? 'Creating…' : 'Create the resident'}</button></div>
          )}
          {d === 'AMBIGUOUS' && (
            <div className="btns">
              <button type="button" className="pa-open" disabled={!canAct} aria-describedby={createBlocker ? 'new-resident-blocked' : undefined} onClick={() => create('CREATE_ANYWAY')}>Different person — create anyway</button>
              {!sentToReview && <button type="button" className="pa-link" disabled={!canAct} onClick={() => create()}>Not sure: send to Identity review</button>}
            </div>
          )}
          {d === 'IDENTITY_CONFLICT' && !sentToReview && (
            <div className="btns"><button type="button" className="pa-open" disabled={!canAct} aria-describedby={createBlocker ? 'new-resident-blocked' : undefined} onClick={() => create()}>Send to Identity review</button></div>
          )}
          {d !== 'MATCH_EXISTING' && blockerLine}
          {sentToReview && (
            <p className="pa-notice" role="status">
              Sent to Identity review with your reason. Nothing was created.{' '}
              {reviewer
                ? <a className="pa-link" href="#identity-review" onClick={(e) => { e.preventDefault(); const el = document.getElementById('identity-review'); if (el) { el.focus(); if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' }); } }}>Go to Identity review</a>
                : 'A PA manager rules on it there.'}
            </p>
          )}
        </div>
      )}
      {card && <ResidentCard t={card} onOpenCase={onOpen} />}
    </section>
  );
}

/** Why no ruling can be made on this environment: the server did not register the route at all (a 404 that said only "Request failed"). */
export const RULING_OFF = 'Ruling on identity reviews is switched off on this environment: the resident write switch on the server does not include review. Tell an administrator.';
export const NOTE_FIRST = 'Write a note first (what you checked).';
export const MERGED_REASON = 'This record was merged into another resident, so a ruling cannot name it. Rule on the record it became, from a new review.';
export const NAME_CE_REASON = 'Naming the resident puts the CounselEAR id on them, which needs the CounselEAR-id permission. Ask an administrator, or rule Different people / Waive.';
// apiRequest never throws: a dropped connection comes back as success:false with no status. A dropped connection or a
// 5xx can come AFTER the answer committed, so it never says "nothing was recorded" -- only a definitive refusal does.
export const OUTCOME_UNKNOWN = 'We could not confirm whether this went through (the connection dropped or the server failed). Refresh the list before answering again: if the review is gone, your answer was saved.';
const outcomeUnknown = (r: { status?: number }) => r.status === undefined || r.status >= 500;
const refusedText = (r: Parameters<typeof errText>[0], nothing: string): string => (outcomeUnknown(r) ? OUTCOME_UNKNOWN : `${nothing} ${errText(r)}`);
const RESOLUTION_LABEL: Record<string, string> = { RESOLVED_SAME_PERSON: 'Same person', RESOLVED_DISTINCT: 'Different people', WAIVED: 'Waived' };

/**
 * `canRule` is `can.review || can.review_counselear_arrival` from GET /residents: held AND the server's write
 * switch open. When it is false every ruling button is greyed out and says why beside it. Omitted = not known = open.
 */
export function IdentityReviews({ arrivalOnly = false, reloadToken = 0, canRule = true, canNameResident = true }: { arrivalOnly?: boolean; reloadToken?: number; canRule?: boolean; canNameResident?: boolean } = {}) {
  const [items, setItems] = useState<Review[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // A refusal is shown beside the review it came from (not only at the top of a long card), and cleared by the next attempt.
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const [attention, setAttention] = useState<Review[]>([]);
  const [attentionTotal, setAttentionTotal] = useState(0);
  // Round 8: the attention list pages (50 at a time, with its total), so rulings past the first page are reachable.
  const ATTENTION_PAGE = 50;
  const loadAttention = async (offset: number) => {
    try {
      const a = await residentsApi.reviews('ATTENTION', { limit: ATTENTION_PAGE, offset });
      const page = a.success && a.data && Array.isArray(a.data.items) ? a.data.items : [];
      const total = a.success && a.data && typeof a.data.total === 'number' ? a.data.total : null;
      setAttention((prev) => {
        const kept = offset === 0 ? [] : prev;
        const seen = new Set(kept.map((r) => r.id));
        return [...kept, ...page.filter((r) => !seen.has(r.id))];
      });
      setAttentionTotal((prev) => (total !== null ? total : offset === 0 ? page.length : Math.max(prev, offset + page.length)));
    } catch { if (offset === 0) { setAttention([]); setAttentionTotal(0); } }
  };
  const load = async () => {
    try { const r = await residentsApi.reviews('OPEN'); if (r.success && r.data) { setItems(r.data.items); setErr(null); } else setErr(errText(r)); }
    catch { setErr('Could not reach the server.'); }
    // Rulings whose CounselEAR id can no longer be attached: shown so a manager can re-open them.
    await loadAttention(0);
  };
  const reopen = async (id: string) => {
    setBusy(id); setRowErr((m) => ({ ...m, [id]: '' })); setNotice(null);
    try {
      const r = await residentsApi.reopenReview(id, { note: (note[id] || '').trim() });
      if (!r.success) { setRowErr((m) => ({ ...m, [id]: refusedText(r, 'Nothing was changed.') })); return; }
      setNotice(r.data && r.data.replayed ? 'This ruling had already been re-opened. Nothing new was changed; rule on the open review below.' : 'Re-opened as a new review. Rule on it below.');
      await load();
    } catch { setRowErr((m) => ({ ...m, [id]: OUTCOME_UNKNOWN })); } finally { setBusy(null); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [reloadToken]);
  const resolve = async (id: string, resolution: 'RESOLVED_SAME_PERSON' | 'RESOLVED_DISTINCT' | 'WAIVED', patientId?: string) => {
    setBusy(id); setRowErr((m) => ({ ...m, [id]: '' })); setNotice(null);
    try {
      const r = await residentsApi.resolveReview(id, { resolution, note: (note[id] || '').trim(), ...(patientId ? { patient_id: patientId } : {}) });
      if (!r.success) { setRowErr((m) => ({ ...m, [id]: refusedText(r, 'Nothing was recorded.') })); return; }
      if (r.data && r.data.replayed) {
        // The review was no longer open: the server kept the earlier answer and saved nothing of this one.
        const was = RESOLUTION_LABEL[r.data.review_state] || r.data.review_state;
        setNotice(r.data.review_state === resolution
          ? `This review had already been answered "${was}", the same as your answer. Nothing new was saved.`
          : `Someone had already answered this review "${was}" before you. Your answer and note were not saved.`);
      } else {
        // The ruling is recorded; say so in words (the review then leaves this list), and when the CounselEAR id waits.
        setNotice(r.data && r.data.attach_pending ? (r.data.message || 'Same person recorded; the CounselEAR id was not attached yet.') : 'Your answer was recorded with your name. The review is no longer waiting.');
      }
      await load();
    }
    catch { setRowErr((m) => ({ ...m, [id]: OUTCOME_UNKNOWN })); } finally { setBusy(null); }
  };
  // Why a ruling button is greyed out, said beside it -- never a silent disabled button.
  const blocker = (id: string): string | null => (!canRule ? RULING_OFF : busy === id ? 'Saving your answer…' : !(note[id] || '').trim() ? NOTE_FIRST : null);
  const blockedId = (id: string) => `review-blocked-${id}`;
  return (
    <section className="pa-card" aria-label="Identity review" id="identity-review" tabIndex={-1}>
      <div className="pa-card-head"><h2>Identity review</h2><span className="why">{arrivalOnly
        ? 'Residents arriving from CounselEAR whom eMerge could not place alone: say whether one of the residents shown is the same person. Nothing is merged; your answer is recorded with your name.'
        : 'Where the resolver would not decide alone: the same SSN with a different date of birth, one CounselEAR id on two rows, a name and date that match. Nothing is merged; your answer is recorded with your name.'}</span></div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
      {!canRule && <p className="pa-notice stop" role="status">{RULING_OFF}</p>}
      {notice && <p className="pa-notice" role="status">{notice}</p>}
      {items && items.length === 0 && <p className="pa-text none" style={{ padding: 14 }}>Nothing waiting.</p>}
      {items && items.map((r) => (
        <div key={r.id} className="pa-section" id={`review-${r.id}`} tabIndex={-1}>
          <h3>{KIND_LABEL[r.kind]} · {DECISION_LABEL[r.decision]} · seen {shortDate(r.created_at)}</h3>
          {r.reason && <p className="pa-text none">Entered reason: {r.reason}</p>}
          {r.ce_create && (
            // A CounselEAR create review: who arrived, so the reviewer can say whether they are one of the residents below.
            <p className="pa-text" aria-label="Incoming from CounselEAR">
              <strong>Incoming from CounselEAR:</strong>{' '}
              {r.incoming
                ? <>{[r.incoming.first_name, r.incoming.last_name].filter(Boolean).join(' ') || 'Name not recorded'}{' · '}DOB {r.incoming.dob ? shortDate(r.incoming.dob) : 'not recorded'}{' · '}CounselEAR {r.incoming.counselear_id}</>
                : <>CounselEAR {r.counselear_id || 'id not recorded'} · the name and date of birth were not kept with this review; look them up in CounselEAR</>}
            </p>
          )}
          <ul className="pa-candidates">
            {r.subject && 'first_name' in r.subject && <CandidateCard c={{ ...(r.subject as Candidate), signals: null }} />}
            {r.candidates.map((c) => (
              <CandidateCard key={c.id} c={c} action={r.ce_create ? (
                // Same person, NAMED: the CounselEAR id goes onto this resident when you confirm.
                <>
                  <button type="button" className="pa-open" disabled={Boolean(blocker(r.id)) || Boolean(c.merged_into) || !canNameResident}
                    onClick={() => resolve(r.id, 'RESOLVED_SAME_PERSON', c.id)}
                    aria-describedby={c.merged_into ? `review-merged-${r.id}-${c.id}` : !canNameResident ? `review-name-ce-${r.id}-${c.id}` : blocker(r.id) ? blockedId(r.id) : undefined}
                    aria-label={c.merged_into ? `Merged into another record: ${[c.first_name, c.last_name].filter(Boolean).join(' ') || 'name not recorded'}` : `Same person as this resident: ${[c.first_name, c.last_name].filter(Boolean).join(' ') || 'name not recorded'}`}>{c.merged_into ? 'Merged into another record' : 'Same person as this resident'}</button>
                  {c.merged_into && <p className="pa-text none" id={`review-merged-${r.id}-${c.id}`}>{MERGED_REASON}</p>}
                  {!c.merged_into && !canNameResident && <p className="pa-text none" id={`review-name-ce-${r.id}-${c.id}`}>{NAME_CE_REASON}</p>}
                </>
              ) : undefined} />
            ))}
          </ul>
          <label className="pa-form"><span>Your note</span><input value={note[r.id] || ''} onChange={(e) => setNote({ ...note, [r.id]: e.target.value })} maxLength={2000} aria-label={`Your note (resolution${r.counselear_id ? `, CounselEAR ${r.counselear_id}` : ''})`} /></label>
          <div className="btns">
            {!r.ce_create && <button type="button" className="pa-open" disabled={Boolean(blocker(r.id))} aria-describedby={blocker(r.id) ? blockedId(r.id) : undefined} onClick={() => resolve(r.id, 'RESOLVED_SAME_PERSON')}>Same person</button>}
            <button type="button" className="pa-open" disabled={Boolean(blocker(r.id))} aria-describedby={blocker(r.id) ? blockedId(r.id) : undefined} onClick={() => resolve(r.id, 'RESOLVED_DISTINCT')}>Different people</button>
            <button type="button" className="pa-link" disabled={Boolean(blocker(r.id))} aria-describedby={blocker(r.id) ? blockedId(r.id) : undefined} onClick={() => resolve(r.id, 'WAIVED')}>Waive</button>
          </div>
          {blocker(r.id) && <p className="pa-text none" id={blockedId(r.id)}>{blocker(r.id)}</p>}
          {rowErr[r.id] && <p className="pa-notice stop" role="alert">{rowErr[r.id]}</p>}
        </div>
      ))}
      {attention.length > 0 && (
        <section className="pa-section" role="region" aria-labelledby="resident-needs-attention-heading">
          <h3 id="resident-needs-attention-heading">Needs attention: a "same person" ruling that cannot be completed</h3>
          {attention.map((r) => (
            <div key={r.id} className="pa-section">
              <p className="pa-text">CounselEAR {r.counselear_id || 'id not recorded'}{' · '}{r.attach && r.attach.message ? r.attach.message : 'The CounselEAR id cannot be attached.'}</p>
              <label className="pa-form"><span>Your note</span><input value={note[r.id] || ''} onChange={(e) => setNote({ ...note, [r.id]: e.target.value })} maxLength={2000} aria-label={`Your note (re-open, CounselEAR ${r.counselear_id || r.id})`} /></label>
              <div className="btns">
                <button type="button" className="pa-open" disabled={Boolean(blocker(r.id))} aria-describedby={blocker(r.id) ? blockedId(r.id) : undefined} onClick={() => reopen(r.id)} aria-label={`Re-open review for CounselEAR ${r.counselear_id || r.id}`}>Re-open review</button>
              </div>
              {blocker(r.id) && <p className="pa-text none" id={blockedId(r.id)}>{blocker(r.id)}</p>}
              {rowErr[r.id] && <p className="pa-notice stop" role="alert">{rowErr[r.id]}</p>}
            </div>
          ))}
          <p className="pa-text none" aria-live="polite">Showing {attention.length} of {Math.max(attentionTotal, attention.length)}</p>
          {attentionTotal > attention.length && (
            <div className="btns">
              <button type="button" className="pa-link" onClick={() => loadAttention(attention.length)}>Show more</button>
            </div>
          )}
        </section>
      )}
    </section>
  );
}

export default function ResidentsView({ identity, onCreated, onOpen, canCorrectDob = false }: { identity: IdentityScope; onCreated: (patientId: string, msg: string) => void; onOpen?: (t: ResidentTarget) => void; canCorrectDob?: boolean }) {
  // A create the resolver would not decide opens a review; the review list below is re-read so it is there.
  const [reviewsToken, setReviewsToken] = useState(0);
  return (
    <div className="pa-residents">
      {identity.can.create && <NewResidentForm onCreated={onCreated} onOpen={onOpen} canCorrectDob={canCorrectDob} reviewer={Boolean(identity.held.review)} onSentToReview={() => setReviewsToken((n) => n + 1)} />}
      {identity.held.create && !identity.can.create && <p className="pa-notice" role="status">Creating residents in eMerge is not open on this environment yet. Tell an administrator: the server&apos;s resident write switch does not include creating.</p>}
      {/* The manifest reviewer holds only the CounselEAR-arrival review (ruling 2026-10-13); the server scopes her list. */}
      {(identity.held.review || identity.held.review_counselear_arrival) && <IdentityReviews arrivalOnly={!identity.held.review} reloadToken={reviewsToken} canRule={Boolean(identity.can.review || identity.can.review_counselear_arrival)} canNameResident={!identity.held.review || Boolean(identity.held.set_counselear_id)} />}
    </div>
  );
}
